import { useState, useCallback } from 'react';
import { useWallet } from '@/contexts/WalletContext';
import { useWallet as useSolanaWallet, useConnection } from '@solana/wallet-adapter-react';
import { supabase } from '@/integrations/supabase/client';
import { buildBondingCurveTradeTransaction } from '@/lib/solana-transactions';
import { validateCompleteTokenDeployment } from '@/lib/anchor/validation';
import { toast } from './use-toast';

export interface RebalancingRecommendation {
  coinId: string;
  token: string;
  currentWeight: number;
  targetWeight: number;
  action: 'Buy' | 'Sell';
  amount: number; // SOL amount for buys, token amount for sells
}

export interface TradeStatus {
  recommendation: RebalancingRecommendation;
  status: 'pending' | 'completed' | 'failed';
  txSignature?: string;
  error?: string;
}

export interface RebalancingProgress {
  total: number;
  completed: number;
  failed: number;
  current?: string;
  trades: TradeStatus[];
}

export interface RebalancingSummary {
  success: boolean;
  executed: number;
  failed: number;
  errors: string[];
}

export interface ExecuteTradeResult {
  success: boolean;
  tradeId: string;
  tradeType: string;
  tokensOut: number;
  solOut: number;
  fee: number;
  newPrice: number;
  newMarketCap: number;
  txSignature: string;
  explorerUrl: string;
}

/**
 * Custom hook for executing portfolio rebalancing trades
 *
 * Handles the complete rebalancing workflow:
 * - Batch execution of buy/sell orders based on optimization recommendations
 * - Real-time progress tracking and status updates
 * - Automatic retry logic for failed trades
 * - On-chain validation and transaction management
 * - Integration with Solana blockchain and bonding curve contracts
 *
 * @returns Object containing rebalancing control functions and progress state
 */
export function useRebalancingExecutor() {
  const { connected, balance } = useWallet();
  const { connection } = useConnection();
  const wallet = useSolanaWallet();
  const [isExecuting, setIsExecuting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [progress, setProgress] = useState<RebalancingProgress>({
    total: 0,
    completed: 0,
    failed: 0,
    trades: [],
  });

  const executeSingleTrade = useCallback(async (
    coinId: string,
    tradeType: 'buy' | 'sell',
    amount: number
  ): Promise<ExecuteTradeResult> => {
    if (!connected || !wallet.publicKey || !wallet.signTransaction) {
      throw new Error('Wallet not connected');
    }

    if (!connection) {
      throw new Error('Connection not available');
    }

    try {
      // Get token mint address from database
      const { data: coin } = await supabase
        .from('coins')
        .select('mint_address, token_config_pda, pool_pda')
        .eq('id', coinId)
        .single();

      const mintAddress = coin?.mint_address;
      if (!mintAddress) {
        throw new Error('Token not deployed on-chain - strict on-chain enforcement required');
      }

      // Strict on-chain verification: Validate complete token deployment
      const validationResult = await validateCompleteTokenDeployment(
        connection,
        mintAddress,
        coin?.token_config_pda,
        coin?.pool_pda
      );

      if (!validationResult.isValid) {
        const errorMsg = validationResult.errors.join(', ');
        throw new Error(`On-chain validation failed: ${errorMsg}`);
      }

      // Build transaction
      const transaction = await buildBondingCurveTradeTransaction(connection, wallet, {
        coinId,
        tradeType,
        amount: tradeType === 'buy'
          ? BigInt(Math.floor(amount * 1e9)) // SOL to lamports
          : BigInt(Math.floor(amount * 1_000_000 * 1_000_000_000)), // Token amount in smallest units
        userWallet: wallet.publicKey,
        tokenMintAddress: new (await import('@solana/web3.js')).PublicKey(mintAddress),
        slippageTolerance: 0.5,
        priorityFee: undefined,
      });

      // Sign transaction
      const signedTransaction = await wallet.signTransaction(transaction);

      // Serialize transaction for sending
      const serializedTransaction = signedTransaction.serialize();
      const signature = await connection.sendRawTransaction(serializedTransaction, {
        skipPreflight: false,
        preflightCommitment: 'confirmed',
      });

      // Monitor confirmation
      await connection.confirmTransaction(signature, 'confirmed');

      // Call edge function to update database
      const { data: result, error: functionError } = await supabase.functions.invoke('execute-trade', {
        body: {
          coinId,
          tradeType,
          amount,
          userWallet: wallet.publicKey.toString(),
          txSignature: signature,
        },
      });

      if (functionError) {
        throw new Error(functionError.message || 'Database update failed');
      }

      if (!result?.success) {
        throw new Error(result?.error || 'Trade failed');
      }

      return {
        success: true,
        tradeId: result.tradeId,
        tradeType: result.tradeType,
        tokensOut: result.tokensOut,
        solOut: result.solOut,
        fee: result.fee,
        newPrice: result.newPrice,
        newMarketCap: result.newMarketCap,
        txSignature: signature,
        explorerUrl: `https://explorer.solana.com/tx/${signature}?cluster=devnet`,
      };
    } catch (error) {
      console.error('Trade execution failed:', error);
      throw new Error(error instanceof Error ? error.message : 'Trade execution failed');
    }
  }, [connected, connection, wallet]);

  /**
   * Executes a batch of rebalancing trades based on optimization recommendations
   *
   * Process:
   * 1. Validates wallet connection and recommendations
   * 2. Separates sells from buys (sells executed first to free up capital)
   * 3. Executes trades sequentially with progress tracking
   * 4. Implements automatic retry logic for failed trades
   * 5. Updates progress state and provides detailed success/failure reporting
   *
   * @param recommendations - Array of trade recommendations from portfolio optimization
   * @returns Promise resolving to execution summary with success/failure counts
   */
  const executeRebalancing = useCallback(async (
    recommendations: RebalancingRecommendation[]
  ): Promise<RebalancingSummary> => {
    if (!connected) {
      throw new Error('Wallet not connected');
    }

    if (!recommendations.length) {
      throw new Error('No rebalancing recommendations provided');
    }

    setIsExecuting(true);

    // Initialize trade statuses
    const initialTrades: TradeStatus[] = recommendations.map(rec => ({
      recommendation: rec,
      status: 'pending',
    }));

    setProgress({
      total: recommendations.length,
      completed: 0,
      failed: 0,
      trades: initialTrades,
    });

    const summary: RebalancingSummary = {
      success: true,
      executed: 0,
      failed: 0,
      errors: [],
    };

    try {
      // Execute sells before buys to free up capital for subsequent purchases
      // This prevents insufficient balance errors during rebalancing
      const sells = recommendations.filter(r => r.action === 'Sell');
      const buys = recommendations.filter(r => r.action === 'Buy');

      const allTrades = [...sells, ...buys];

      // Execute trades sequentially to maintain proper order and avoid conflicts
      // Parallel execution could cause nonce conflicts or insufficient balance issues
      for (let i = 0; i < allTrades.length; i++) {
        const rec = allTrades[i];
        setProgress(prev => ({
          ...prev,
          current: `${rec.action} ${rec.amount.toFixed(4)} ${rec.action === 'Buy' ? 'SOL' : rec.token}`,
        }));

        // Update UI to show current trade is being processed
        setProgress(prev => ({
          ...prev,
          trades: prev.trades.map(trade =>
            trade.recommendation === rec ? { ...trade, status: 'pending' as const } : trade
          ),
        }));

        try {
          // Execute the individual trade through the bonding curve contract
          const result = await executeSingleTrade(
            rec.coinId,
            rec.action.toLowerCase() as 'buy' | 'sell',
            rec.amount
          );

          if (result.success) {
            summary.executed++;
            setProgress(prev => ({
              ...prev,
              completed: prev.completed + 1,
              trades: prev.trades.map(trade =>
                trade.recommendation === rec
                  ? { ...trade, status: 'completed', txSignature: result.txSignature }
                  : trade
              ),
            }));
          } else {
            throw new Error('Trade failed');
          }

        } catch (error) {
          console.error(`Trade failed for ${rec.token}:`, error);
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';

          summary.failed++;
          summary.errors.push(`${rec.action} ${rec.token}: ${errorMessage}`);
          summary.success = false;

          setProgress(prev => ({
            ...prev,
            failed: prev.failed + 1,
            trades: prev.trades.map(trade =>
              trade.recommendation === rec
                ? { ...trade, status: 'failed', error: errorMessage }
                : trade
            ),
          }));

          // Implement automatic retry logic for failed trades
          // Network issues or temporary on-chain conflicts may cause failures
          try {
            setProgress(prev => ({
              ...prev,
              current: `Retrying ${rec.action} ${rec.amount.toFixed(4)} ${rec.action === 'Buy' ? 'SOL' : rec.token}`,
            }));

            const result = await executeSingleTrade(
              rec.coinId,
              rec.action.toLowerCase() as 'buy' | 'sell',
              rec.amount
            );

            if (result.success) {
              // Update counters to reflect successful retry
              summary.executed++;
              summary.failed--;
              summary.errors.pop(); // Remove the original error
              summary.success = summary.failed === 0; // All trades now successful

              setProgress(prev => ({
                ...prev,
                completed: prev.completed + 1,
                failed: prev.failed - 1,
                trades: prev.trades.map(trade =>
                  trade.recommendation === rec
                    ? { ...trade, status: 'completed', txSignature: result.txSignature, error: undefined }
                    : trade
                ),
              }));
            }
          } catch (retryError) {
            console.error(`Retry failed for ${rec.token}:`, retryError);
            // Retry failed - trade remains in failed state
          }
        }
      }

      // Final summary
      if (summary.success) {
        toast({
          title: 'Rebalancing Complete',
          description: `Successfully executed ${summary.executed} trades`,
        });
      } else {
        toast({
          title: 'Rebalancing Partially Complete',
          description: `${summary.executed} successful, ${summary.failed} failed`,
          variant: 'destructive',
        });
      }

    } catch (error) {
      console.error('Rebalancing execution failed:', error);
      summary.success = false;
      summary.errors.push(error instanceof Error ? error.message : 'Unknown error');

      toast({
        title: 'Rebalancing Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsExecuting(false);
      setProgress(prev => ({
        ...prev,
        current: undefined,
      }));
    }

    return summary;
  }, [connected, executeSingleTrade]);

  const cancelRebalancing = useCallback(() => {
    setIsCancelling(true);
    setIsExecuting(false);
    toast({
      title: 'Rebalancing Cancelled',
      description: 'Remaining trades have been cancelled',
    });
  }, []);

  return {
    executeRebalancing,
    cancelRebalancing,
    isExecuting,
    isCancelling,
    progress,
  };
}